import { cn } from "@/lib/utils";

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn("skeleton rounded", className)}
      aria-hidden="true"
    />
  );
}

export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-card p-6 space-y-4",
        className,
      )}
      aria-hidden="true"
    >
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-10 rounded-lg" />
      </div>
      <Skeleton className="h-8 w-32" />
    </div>
  );
}

/**
 * Single KPI cell skeleton — matches the editorial KPI strip cells.
 */
export function SkeletonStat({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        "p-5 md:p-6 flex flex-col gap-3 min-h-[112px]",
        className,
      )}
      aria-hidden="true"
    >
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="w-7 h-7 rounded-lg" />
      </div>
      <Skeleton className="h-9 w-24" />
    </div>
  );
}

/**
 * One table row skeleton — matches initiatives-table row geometry.
 */
export function SkeletonRow({ cols = 6 }: { cols?: number }) {
  return (
    <div
      className="flex items-center gap-4 px-5 md:px-6 py-4 border-b border-border last:border-b-0"
      aria-hidden="true"
    >
      <Skeleton className="h-4 flex-[2]" />
      {Array.from({ length: Math.max(0, cols - 2) }).map((_, i) => (
        <Skeleton key={i} className="h-4 flex-1" />
      ))}
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>
  );
}

/**
 * Chart placeholder skeleton — height matches typical chart widget.
 */
export function SkeletonChart({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-card p-5 md:p-6 space-y-4",
        className,
      )}
      aria-hidden="true"
    >
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-12" />
      </div>
      <Skeleton className="h-48 w-full rounded-xl" />
    </div>
  );
}

export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div
      className="rounded-2xl border border-border bg-card overflow-hidden"
      aria-hidden="true"
    >
      <div className="p-5 md:p-6 border-b border-border">
        <Skeleton className="h-6 w-40" />
      </div>
      <div>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    </div>
  );
}
